import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ReactSelect from "react-select";
import {
  X,
  Wallet,
  Sparkles,
  User,
  IndianRupee,
  CheckCircle2,
} from "lucide-react";
import { createManualTeamMemberPayment } from "../../../../Redux/ApiCalls/Dashboard/TeamMemberPaymentApi";
import type { Member } from "../../../../Data/member.dto";
import BrandDatePicker from "../../Utlis/BrandDatePicker";

const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];

const PAYMENT_TYPES = [
  { value: "salary", label: "Salary" },
  { value: "bonus", label: "Bonus" },
  { value: "advance", label: "Advance" },
  { value: "reimbursement", label: "Reimbursement" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "completed", label: "Completed", tone: "#31a66a" },
  { value: "pending", label: "Pending", tone: "#f09a3e" },
  { value: "failed", label: "Failed", tone: "#e44862" },
];

interface CreateManualPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  members: Member[];
  onSuccess?: () => void;
  preselectedMemberId?: string;
}

export default function CreateManualPaymentModal({
  isOpen,
  onClose,
  members,
  onSuccess,
  preselectedMemberId,
}: CreateManualPaymentModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    team_member_id: "",
    amount: "",
    payment_method: "bank_transfer",
    payment_date: new Date().toISOString().split("T")[0],
    payment_type: "salary",
    transaction_reference: "",
    description: "",
    notes: "",
    status: "pending",
  });

  useEffect(() => {
    if (isOpen && preselectedMemberId) {
      setFormData((prev) => ({ ...prev, team_member_id: preselectedMemberId }));
    }
  }, [isOpen, preselectedMemberId]);

  const handleChange = (
    e: { target: { name: string; value: string } }
  ) => {
    setError("");
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    if (!formData.team_member_id.trim()) return "Please select a team member.";
    if (!formData.amount.trim()) return "Amount is required.";
    if (Number(formData.amount) <= 0) return "Amount must be greater than 0.";
    if (!formData.payment_date.trim()) return "Payment date is required.";
    const todayStr = new Date().toISOString().split("T")[0];
    if (formData.payment_date > todayStr) return "Payment date cannot be in the future.";
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await createManualTeamMemberPayment(formData);
      if (result != null) {
        setFormData({
          team_member_id: "",
          amount: "",
          payment_method: "bank_transfer",
          payment_date: new Date().toISOString().split("T")[0],
          payment_type: "salary",
          transaction_reference: "",
          description: "",
          notes: "",
          status: "pending",
        });
        onSuccess?.();
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedMember = members.find((m) => String(m.id) === formData.team_member_id);

  return (
    <Overlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroTop>
            <HeroIcon>
              <Wallet size={22} />
            </HeroIcon>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                Team payout
              </HeroBadge>
              <ModalTitle>Record payment</ModalTitle>
              <ModalSubtitle>
                Log a salary, bonus, or advance for your team member.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroTop>
        </ModalHero>

        <ModalBody>
          {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

          {selectedMember && (
            <MemberChip>
              <User size={14} />
              Paying <strong>{selectedMember.name}</strong>
              {selectedMember.job_type ? ` · ${selectedMember.job_type}` : ""}
            </MemberChip>
          )}

          <form onSubmit={handleSubmit}>
            <SectionLabel>Payment details</SectionLabel>
            <FieldGrid>
              <FieldFull>
                <Label>
                  Team member<Required>*</Required>
                </Label>
                <Select
                  name="team_member_id"
                  value={formData.team_member_id}
                  onChange={handleChange}
                  disabled={!!preselectedMemberId}
                >
                  <option value="">Select team member</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} {m.job_type ? `(${m.job_type})` : ""}
                    </option>
                  ))}
                </Select>
              </FieldFull>

              <Field>
                <Label>
                  Amount<Required>*</Required>
                </Label>
                <InputWrap>
                  <IndianRupee size={16} color="#c94daa" />
                  <Input
                    type="number"
                    name="amount"
                    step="0.01"
                    min="0"
                    placeholder="5000.00"
                    value={formData.amount}
                    onChange={handleChange}
                  />
                </InputWrap>
              </Field>

              <Field>
                <Label>
                  Payment date<Required>*</Required>
                </Label>
                <BrandDatePicker
                  name="payment_date"
                  value={formData.payment_date}
                  maxDate={new Date().toISOString().split("T")[0]}
                  required
                  onChange={(v) => {
                    setError("");
                    setFormData((prev) => ({ ...prev, payment_date: v }));
                  }}
                />
              </Field>

              <Field>
                <Label>Payment method</Label>
                <Select name="payment_method" value={formData.payment_method} onChange={handleChange}>
                  {PAYMENT_METHODS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </Field>

              <Field>
                <Label>Payment type</Label>
                <Select name="payment_type" value={formData.payment_type} onChange={handleChange}>
                  {PAYMENT_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </Field>
            </FieldGrid>

            <SectionLabel>Additional info</SectionLabel>
            <FieldGrid>
              <FieldFull>
                <Label>Transaction reference</Label>
                <Input
                  type="text"
                  name="transaction_reference"
                  placeholder="e.g. UPI123456789"
                  value={formData.transaction_reference}
                  onChange={handleChange}
                />
              </FieldFull>

              <FieldFull>
                <Label>Description</Label>
                <Input
                  type="text"
                  name="description"
                  placeholder="e.g. January 2024 salary payment"
                  value={formData.description}
                  onChange={handleChange}
                />
              </FieldFull>

              <FieldFull>
                <Label>Notes</Label>
                <TextArea
                  name="notes"
                  rows={2}
                  placeholder="e.g. Paid via UPI"
                  value={formData.notes}
                  onChange={handleChange}
                />
              </FieldFull>
            </FieldGrid>

            <SectionLabel>Status</SectionLabel>
            <StatusRow>
              {STATUS_OPTIONS.map((opt) => (
                <StatusChip
                  key={opt.value}
                  type="button"
                  $active={formData.status === opt.value}
                  $tone={opt.tone}
                  onClick={() => {
                    setError("");
                    setFormData((prev) => ({ ...prev, status: opt.value }));
                  }}
                >
                  {opt.label}
                </StatusChip>
              ))}
            </StatusRow>

            <ActionRow>
              <CancelBtn type="button" onClick={onClose}>Cancel</CancelBtn>
              <SubmitBtn type="submit" disabled={loading}>
                {loading ? "Submitting..." : (
                  <>
                    <CheckCircle2 size={16} />
                    Create payment
                  </>
                )}
              </SubmitBtn>
            </ActionRow>
          </form>
        </ModalBody>
      </ModalBox>
    </Overlay>
  );
}

const gradient = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

type SelectProps = {
  name?: string;
  value: string | number;
  onChange: (e: { target: { name: string; value: string } }) => void;
  children: React.ReactNode;
  disabled?: boolean;
};

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "rgba(201, 77, 170, 0.4)" : "rgba(23, 23, 23, 0.1)",
    background: state.isFocused ? "#ffffff" : "#fafafa",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(201, 77, 170, 0.1)" : "none",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: "#171717",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
    "&:hover": {
      borderColor: state.isDisabled ? "rgba(23, 23, 23, 0.1)" : "rgba(201, 77, 170, 0.35)",
      background: state.isDisabled ? "#fafafa" : "#ffffff",
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 14px",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "rgba(23, 23, 23, 0.35)",
    fontWeight: 500,
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#171717",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isFocused ? "#c94daa" : "rgba(23, 23, 23, 0.45)",
    padding: "0 12px 0 8px",
    transition: "transform 0.15s ease, color 0.15s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
  }),
  menu: (base: any) => ({
    ...base,
    marginTop: "8px",
    borderRadius: "16px",
    padding: "6px",
    border: "1px solid rgba(201, 77, 170, 0.12)",
    boxShadow: "0 18px 36px rgba(23, 23, 23, 0.14)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuList: (base: any) => ({
    ...base,
    padding: 0,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base: any, state: any) => ({
    ...base,
    borderRadius: "12px",
    padding: "11px 13px",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "14px",
    fontWeight: state.isSelected ? 700 : 600,
    color: "#171717",
    background: state.isSelected
      ? "linear-gradient(135deg, rgba(255, 201, 81, 0.18), rgba(201, 77, 170, 0.16))"
      : state.isFocused
        ? "rgba(201, 77, 170, 0.08)"
        : "#ffffff",
  }),
};

const Select: React.FC<SelectProps> = ({ name, value, onChange, children, disabled }) => {
  const options: Array<{ value: string; label: React.ReactNode; isDisabled?: boolean }> = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement<any>(child) && child.type === "option") {
      options.push({
        value: String(child.props.value),
        label: child.props.children,
        isDisabled: child.props.disabled,
      });
    } else if (Array.isArray(child)) {
      child.forEach((nestedChild) => {
        if (React.isValidElement<any>(nestedChild) && nestedChild.type === "option") {
          options.push({
            value: String(nestedChild.props.value),
            label: nestedChild.props.children,
            isDisabled: nestedChild.props.disabled,
          });
        }
      });
    }
  });

  const selectedOption = options.find((option) => option.value === String(value)) ?? null;
  const placeholderOption = options.find((option) => option.isDisabled) ?? options.find((option) => option.value === "");

  return (
    <ReactSelect
      value={selectedOption}
      onChange={(option) => onChange({ target: { name: name ?? "", value: option?.value ?? "" } })}
      options={options.filter((option) => !option.isDisabled)}
      placeholder={placeholderOption ? String(placeholderOption.label) : "Select an option"}
      styles={selectStyles}
      menuPosition="fixed"
      menuPortalTarget={document.body}
      isSearchable={false}
      isDisabled={disabled}
    />
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 9999;
  padding: 16px;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 540px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
`;

const ModalHero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
  flex-shrink: 0;
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${gradient};
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.25);
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover { color: #c94daa; border-color: rgba(201, 77, 170, 0.25); }
`;

const ModalBody = styled.div`
  padding: 20px 24px 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const ErrorBanner = styled.p`
  margin: 0 0 14px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(228, 72, 98, 0.08);
  border: 1px solid rgba(228, 72, 98, 0.2);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #e44862;
`;

const MemberChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.15);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.72);
  strong { font-weight: 700; color: #171717; }
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 16px 0 10px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  &:first-of-type { margin-top: 0; }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldFull = styled(Field)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.55);
`;

const Required = styled.span`
  color: #e44862;
  margin-left: 2px;
`;

const InputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus-within {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
`;

const Input = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  &:focus { outline: none; }
  &::placeholder { color: rgba(23, 23, 23, 0.35); font-weight: 500; }
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #171717;
  resize: vertical;
  min-height: 72px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
  &::placeholder { color: rgba(23, 23, 23, 0.35); }
`;

const StatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
`;

const StatusChip = styled.button<{ $active: boolean; $tone: string }>`
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? p.$tone : "rgba(23, 23, 23, 0.1)")};
  background: ${(p) => (p.$active ? `${p.$tone}14` : "#fafafa")};
  color: ${(p) => (p.$active ? p.$tone : "rgba(23, 23, 23, 0.55)")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: ${(p) => p.$tone};
    background: ${(p) => `${p.$tone}10`};
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
`;

const CancelBtn = styled.button`
  flex: 0 0 auto;
  padding: 0 20px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: rgba(201, 77, 170, 0.25); color: #c94daa; }
`;

const SubmitBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;
  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
`;
